// ─────────────────────────────────────────────────────────────────────────────
// Frontend Model — CustomApp.js
// Place: src/models/CustomApp.js
// ─────────────────────────────────────────────────────────────────────────────

class CustomApp {
  constructor(data = {}) {
    // ── Identity ───────────────────────────────────────────────────────────
    this.id          = data.id || data._id || null;
    this.appName     = data.appName     || '';
    this.packageName = data.packageName || '';
    this.description = data.description || '';

    // ── Template & Branding ────────────────────────────────────────────────
    this.template       = data.template       || 'template_a';
    this.primaryColor   = data.primaryColor   || '#0D47A1';
    this.secondaryColor = data.secondaryColor || '#1976D2';
    this.accentColor    = data.accentColor    || '#FF5722';

    // ── SDK / Version ──────────────────────────────────────────────────────
    this.version          = data.version          || '1.0.0';
    this.minSdkVersion    = data.minSdkVersion    || '21';
    this.targetSdkVersion = data.targetSdkVersion || '33';

    // ── Features ───────────────────────────────────────────────────────────
    this.features = Array.isArray(data.features) ? data.features : [];

    // ── Status & Stats ─────────────────────────────────────────────────────
    this.status    = data.status    || 'Draft';
    this.builds    = data.builds    || 0;
    this.downloads = data.downloads || 0;

    // ── Assets ─────────────────────────────────────────────────────────────
    this.logoUrl   = data.logoUrl   || null;
    this.splashUrl = data.splashUrl || null;
    this.iconUrl   = data.iconUrl   || null;

    // ── Legacy single APK field ────────────────────────────────────────────
    this.apkUrl       = data.apkUrl       || null;
    this.apkPublicId  = data.apkPublicId  || null;

    // ✅ APK Versions array (from backend apkVersions sub-docs)
    this.apkVersions = Array.isArray(data.apkVersions)
      ? data.apkVersions.map(v => ({
          id:                v._id || v.id,
          versionName:       v.versionName       || '',
          versionCode:       v.versionCode       || 0,
          track:             v.track             || 'production',
          rolloutPercentage: v.rolloutPercentage ?? 100,
          releaseNotes:      v.releaseNotes      || '',
          minSdkVersion:     v.minSdkVersion     || '21',
          targetSdkVersion:  v.targetSdkVersion  || '33',
          fileName:          v.fileName          || '',
          fileSize:          v.fileSize          || '',
          isActive:          v.isActive          ?? false,
          uploadedAt:        v.uploadedAt        ? new Date(v.uploadedAt) : null,
          downloadUrl:       v.apkUrl            || v.downloadUrl || null,
          apkUrl:            v.apkUrl            || null,
        }))
      : [];

    // ✅ latestAPK — computed from apkVersions (active production first)
    this.latestAPK = this._computeLatestAPK(data);

    // ── Build History ──────────────────────────────────────────────────────
    this.buildHistory  = Array.isArray(data.buildHistory) ? data.buildHistory : [];
    this.lastBuildDate = data.lastBuildDate ? new Date(data.lastBuildDate) : null;
    this.lastBuild     = data.lastBuild     ? new Date(data.lastBuild)     : null;
    this.publishedDate = data.publishedDate ? new Date(data.publishedDate) : null;

    // ── Audit ──────────────────────────────────────────────────────────────
    this.createdAt = data.createdAt ? new Date(data.createdAt) : new Date();
    this.updatedAt = data.updatedAt ? new Date(data.updatedAt) : new Date();
    this.createdBy = data.createdBy || null;
    this.updatedBy = data.updatedBy || null;
  }

  // ── Private helper ─────────────────────────────────────────────────────────
  _computeLatestAPK(data) {
    // 1. Use virtual from backend if already resolved
    if (data.latestAPK) {
      const v = data.latestAPK;
      return {
        id:                v._id || v.id,
        versionName:       v.versionName       || '',
        versionCode:       v.versionCode       || 0,
        track:             v.track             || 'production',
        rolloutPercentage: v.rolloutPercentage ?? 100,
        releaseNotes:      v.releaseNotes      || '',
        fileName:          v.fileName          || '',
        fileSize:          v.fileSize          || '',
        isActive:          v.isActive          ?? true,
        uploadedAt:        v.uploadedAt        ? new Date(v.uploadedAt) : null,
        downloadUrl:       v.apkUrl            || v.downloadUrl || null,
      };
    }

    // 2. Compute from apkVersions array
    const versions = Array.isArray(data.apkVersions) ? data.apkVersions : [];
    if (versions.length === 0) return null;

    // Active production first → active any track → most recent
    let pick =
      versions.find(v => v.isActive && v.track === 'production') ||
      versions.find(v => v.isActive) ||
      [...versions].sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt))[0];

    if (!pick) return null;
    return {
      id:                pick._id || pick.id,
      versionName:       pick.versionName       || '',
      versionCode:       pick.versionCode       || 0,
      track:             pick.track             || 'production',
      rolloutPercentage: pick.rolloutPercentage ?? 100,
      releaseNotes:      pick.releaseNotes      || '',
      fileName:          pick.fileName          || '',
      fileSize:          pick.fileSize          || '',
      isActive:          pick.isActive          ?? false,
      uploadedAt:        pick.uploadedAt        ? new Date(pick.uploadedAt) : null,
      downloadUrl:       pick.apkUrl            || pick.downloadUrl || null,
    };
  }

  // ── Validation ─────────────────────────────────────────────────────────────
  validate() {
    const errors = [];

    if (!this.appName?.trim())
      errors.push('App name is required');

    if (!this.packageName?.trim()) {
      errors.push('Package name is required');
    } else if (!/^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)+$/.test(this.packageName)) {
      errors.push('Package name must be in format: com.company.appname (lowercase, no spaces)');
    }

    if (!this.template)
      errors.push('Template selection is required');

    if (this.version && !/^\d+\.\d+\.\d+$/.test(this.version))
      errors.push('Version must be in format: X.Y.Z (e.g., 1.0.0)');

    const hexColor = /^#[0-9A-Fa-f]{6}$/;
    if (this.primaryColor   && !hexColor.test(this.primaryColor))   errors.push('Primary color must be a valid hex color (e.g., #0D47A1)');
    if (this.secondaryColor && !hexColor.test(this.secondaryColor)) errors.push('Secondary color must be a valid hex color');
    if (this.accentColor    && !hexColor.test(this.accentColor))    errors.push('Accent color must be a valid hex color');

    if (this.minSdkVersion    && isNaN(parseInt(this.minSdkVersion)))    errors.push('Minimum SDK version must be a number');
    if (this.targetSdkVersion && isNaN(parseInt(this.targetSdkVersion))) errors.push('Target SDK version must be a number');

    if (!Array.isArray(this.features)) errors.push('Features must be an array');

    return { isValid: errors.length === 0, errors };
  }

  // ── Serialise (for FormData building in controller) ────────────────────────
  toJSON() {
    return {
      id:               this.id,
      appName:          this.appName,
      packageName:      this.packageName,
      template:         this.template,
      primaryColor:     this.primaryColor,
      secondaryColor:   this.secondaryColor,
      accentColor:      this.accentColor,
      description:      this.description,
      version:          this.version,
      minSdkVersion:    this.minSdkVersion,
      targetSdkVersion: this.targetSdkVersion,
      features:         this.features,
      status:           this.status,
      builds:           this.builds,
      downloads:        this.downloads,
      logoUrl:          this.logoUrl,
      splashUrl:        this.splashUrl,
      iconUrl:          this.iconUrl,
      buildHistory:     this.buildHistory,
      lastBuildDate:    this.lastBuildDate,
      createdAt:        this.createdAt,
      updatedAt:        this.updatedAt,
      createdBy:        this.createdBy,
      updatedBy:        this.updatedBy,
    };
  }

  // ── Static factory ─────────────────────────────────────────────────────────
  static fromJSON(json) {
    if (!json) return null;
    return new CustomApp(json);
  }

  // ── Helper getters ─────────────────────────────────────────────────────────
  getStatusColor() {
    return { Published: '#10b981', Building: '#f59e0b', Draft: '#64748b', Failed: '#ef4444' }[this.status] || '#6b7280';
  }

  getTemplateName() {
    return {
      template_a: 'HRMS Template',
      template_b: 'CRM Template',
      template_c: 'Delivery Template',
      template_d: 'E-Commerce',
      template_e: 'Healthcare',
      template_f: 'Education',
      template_g: 'Finance',
      template_h: 'DPC Template',

    }[this.template] || 'Unknown Template';
  }

  canBuild()   { return ['Draft', 'Failed', 'Published'].includes(this.status); }
  canPublish() { return this.status === 'Draft' && this.builds > 0; }
  hasAPK()     { return this.apkVersions.length > 0 || !!this.apkUrl; }

  // ── Mutation helpers (local state only — call API separately) ──────────────
  incrementBuildCount()    { this.builds += 1;    this.lastBuildDate = new Date(); }
  incrementDownloadCount() { this.downloads += 1; }
  updateStatus(s)          { this.status = s;     this.updatedAt = new Date(); }
  clone()                  { return new CustomApp(this.toJSON()); }
}

export default CustomApp;